import React, {memo} from 'react';
import {useSelector} from 'react-redux';
import cn from 'classnames';
import {ForecastDaysMode} from "@/app/constants/forecast";
import {WeatherForecastDay} from '@/app/interfaces/weather';
import {selectTemperatureUnit} from '@/app/store/config/selectors';
import {selectForecastData, selectForecastViewParams} from "@/app/store/forecast/selectors";
import {useLocalizer} from '@/common/services/localizer';
import {getLocalDate} from '@/common/helpers/date/getLocalDate';
import {temperatureDisplayReduce} from '@/common/helpers/unitReducers/temperatureDisplayReduce';
import {RouterLink} from '@/common/components/RouterLink';
import {Tooltip} from '@/common/components/Tooltip';
import {ConditionMediumIcon} from '@/common/components/ConditionMediumIcon';
import styles from './WeatherDailyTab.css';

interface WeatherDailyTabProps extends Pick<WeatherForecastDay, 'date' | 'isHoliday' | 'temperature' | 'condition'> {
	url: string;
	isSelected: boolean;
}

export const WeatherDailyTab = memo<WeatherDailyTabProps>(props => {
	const {date, isHoliday, temperature, condition, url, isSelected} = props;

	const temperatureUnit = useSelector(selectTemperatureUnit);
	const {location, forecast} = useSelector(selectForecastData)!;
	const {daysMode, selectedDate} = useSelector(selectForecastViewParams);

	const t = useLocalizer();

	const [weekday, day, month] = getLocalDate(date, t.language);
	const dayIndex = new Date(date).getDay();

	const ariaLabel = t('ariaLabelWeather', {
		locationIn: location.titleLc,
		weekdayPreposition: t(`weatherWeekdayPrepositions.${dayIndex}`),
		day,
		month,
		temperatureMin: temperatureDisplayReduce(temperature.min, temperatureUnit),
		temperatureMax: temperatureDisplayReduce(temperature.max, temperatureUnit),
	});

	let linkTitle;
	if (daysMode === ForecastDaysMode.Days7 && forecast[0].date === date) {
		linkTitle = t('weatherDailyToday', {locationIn: location.titleLc});
	} else if (daysMode === ForecastDaysMode.Days10 && forecast[0].date === date && date!== selectedDate) {
		linkTitle = t('weatherDaily10Days', {locationIn: location.titleLc});
	} else {
		linkTitle = t('weatherDailyDate', {
			locationIn: location.titleLc,
			weekdayPreposition: t(`weatherWeekdayPrepositions.${dayIndex}`),
			day,
			month,
		});
	}

	return (
		<RouterLink
			className={cn(styles.root, isSelected && styles.isSelected)}
			url={url}
			title={linkTitle}
			role='menuitem'
			aria-label={ariaLabel}>
			<span className={styles.weekDay}>{weekday}</span>
			<span className={cn(styles.day, isHoliday && styles.isHoliday)}>{day}</span>
			<span className={styles.month}>{month}</span>
			<Tooltip className={styles.icon} title={t(`weatherConditions.${condition}`)} positionX='right'>
				<ConditionMediumIcon condition={condition} />
			</Tooltip>
			<div className={styles.extremeWrapper}>
				<div className={styles.extremeItem}>
					<p className={styles.extremeItemName}>{t('min')}</p>
					<p>{temperatureDisplayReduce(temperature.min, temperatureUnit)}</p>
				</div>
				<div className={styles.extremeItem}>
					<p className={styles.extremeItemName}>{t('max')}</p>
					<p>{temperatureDisplayReduce(temperature.max, temperatureUnit)}</p>
				</div>
			</div>
			{isSelected && <div className={styles.selectedFooterFade} />}
		</RouterLink>
	);
});

WeatherDailyTab.displayName = 'WeatherDailyTab';
